/** 
 *  UNITS
 * 
 * This file contains unit types for units where we want to keep track of the exact factor of the unit we're dealing 
 * with, such as dose or length -- e.g. when handling lengths it's important to keep track whether the units are
 * in centimeters or millimeters. The types defined in this file keep track of which unit factor their values are
 * in.
 * 
 * Any units defined in this file should ONLY be handled with these unit types and their respective helper functions
 * throughout the application -- using JavaScript Number objects for values of these units is strongly discouraged.
 * 
 * The getter helper functions defined in this file should be used to get correct values out of these units. Which
 * helper to use depends a bit on the context. For contexts where the factor of the unit will always be the same
 * you should use the getXInY functions (e.g. getDoseInGy). For context where the factor of the unit may depend
 * on how the UI is configured (via e.g. user or clinic preferences) it's better to use the getXValue (e.g. getDoseValue)
 * functions instead.
 * 
 * */

import { has, round } from "lodash-es";
import { SelectedDoseUnit } from "./customization-types";

const RADIATION_DOSE_ROUND_TO_DECIMALS = 2;
const LENGTH_ROUND_TO_DECIMALS = 2;
const VOLUME_ROUND_TO_DECIMALS = 3;

/** Units of dose radiation. NOTE: currently unused. */
export enum RadiationDoseUnit {
    /** Radiation dose unit as greys */
    DoseInGy = 'DoseInGy',
    /** Radiation dose unit as centigreys */
    DoseInCGy = 'DoseInCCGy',
}

/** Units of length */
export enum LengthUnit {
    /** Length unit as millimeters */
    LengthInMm = 'LengthInMm',
    /** Length unit as centimeters */
    LengthInCm = 'LengthInCm',
}

/** Units of Volume */
export enum VolumeUnit {
    /** Volume unit as mm^3 */
    VolumeInMm3 = 'VolumeInMm3',
    VolumeInCm3 = 'VolumeInCm3',
}


/** A value of a radiation dose. NOTE: currently unused. */
export type RadiationDoseValue = {
    /** The value of this radiation dose in greys (Gy) */
    _doseInGy: number;
}

/** A value of a length */
export type LengthValue = {
    /** The value of this length in millimeters (mm) */
    _lengthInMm: number;
}

/** A value of a volume */
export type VolumeValue = {
    /** The value of this volume in mm^3 */
    _volumeInMm3: number;
}



/** Returns value of given dose as a number of given unit */
export const getRadiationDoseValue = (dose: RadiationDoseValue, unit: RadiationDoseUnit): number => {
    switch (unit) {
        case RadiationDoseUnit.DoseInGy:
            return getRadiationDoseInGy(dose);
        case RadiationDoseUnit.DoseInCGy:
            return getRadiationDoseInCGy(dose);
        default:
            throw new Error(`Unsupported unit: ${unit}`);
    }
}

/** Returns value of given length as a number of given unit */
export const getLengthValue = (length: LengthValue, unit: LengthUnit): number => {
    switch (unit) {
        case LengthUnit.LengthInMm:
            return getLengthInMm(length);
        case LengthUnit.LengthInCm:
            return getLengthInCm(length);
        default:
            throw new Error(`Unsupported unit: ${unit}`);
    }
}

/** Returns value of given volume as a number of given unit */
export const getVolumeValue = (volume: VolumeValue, unit: VolumeUnit): number => {
    switch (unit) {
        case VolumeUnit.VolumeInMm3:
            return getVolumeInMm3(volume);
        case VolumeUnit.VolumeInCm3:
            return getVolumeInCm3(volume);
        default:
            throw new Error(`Unsupported unit: ${unit}`);
    }
}


/** Returns the value of given dose in greys. */
export const getRadiationDoseInGy = (dose: RadiationDoseValue): number => dose._doseInGy;

/** Returns the value of given dose in centigreys. */
export const getRadiationDoseInCGy = (dose: RadiationDoseValue): number => dose._doseInGy * 100;

/** Returns the value of given length in millimeters. */
export const getLengthInMm = (length: LengthValue): number => length._lengthInMm;

/** Returns the value of given length in centimeters. */
export const getLengthInCm = (length: LengthValue): number => round(length._lengthInMm / 10, LENGTH_ROUND_TO_DECIMALS);

/** Returns the value of given volume in mm^3. */
export const getVolumeInMm3 = (volume: VolumeValue): number => volume._volumeInMm3;

/** Returns the value of given volume in cm^3. */
export const getVolumeInCm3 = (volume: VolumeValue): number => round(volume._volumeInMm3 / 1000, VOLUME_ROUND_TO_DECIMALS);



/** Creates a RadiationDoseValue object from given value of given unit */
export const createRadiationDose = (value: number, unitOfValue: RadiationDoseUnit): RadiationDoseValue => {
    switch (unitOfValue) {
        case RadiationDoseUnit.DoseInCGy:
            // include extra rounding padding for cGy -> Gy conversion accuracy
            return { _doseInGy: round(value / 100, RADIATION_DOSE_ROUND_TO_DECIMALS + 2) };
        case RadiationDoseUnit.DoseInGy:
            return { _doseInGy: round(value, RADIATION_DOSE_ROUND_TO_DECIMALS) };
        default:
            throw new Error(`Unsupported unit: ${unitOfValue}`);
    }
}

/** Creates a LengthValue object from given value of given unit */
export const createLength = (value: number, unitOfValue: LengthUnit): LengthValue => {
    switch (unitOfValue) {
        case LengthUnit.LengthInMm:
            return { _lengthInMm: round(value, LENGTH_ROUND_TO_DECIMALS) };
        case LengthUnit.LengthInCm:
            return { _lengthInMm: round(value * 10, LENGTH_ROUND_TO_DECIMALS) };
        default:
            throw new Error(`Unsupported unit: ${unitOfValue}`);
    }
}

/** Creates a VolumeValue object from given value of given unit */
export const createVolume = (value: number, unitOfValue: VolumeUnit): VolumeValue => {
    switch (unitOfValue) {
        case VolumeUnit.VolumeInMm3:
            return { _volumeInMm3: round(value, VOLUME_ROUND_TO_DECIMALS) };
        case VolumeUnit.VolumeInCm3:
            return { _volumeInMm3: round(value * 1000, VOLUME_ROUND_TO_DECIMALS) };
        default:
            throw new Error(`Unsupported unit: ${unitOfValue}`);
    }
}


/** Converts a SelectedDoseUnit enum into a RadiationDoseUnit, or throws if cannot convert. */
export const convertSelectedDoseUnitToRadiationDoseUnit = (selectedDoseUnit: SelectedDoseUnit): RadiationDoseUnit => {
    switch (selectedDoseUnit) {
        case SelectedDoseUnit.Gy:
            return RadiationDoseUnit.DoseInGy
        case SelectedDoseUnit.cGy:
            return RadiationDoseUnit.DoseInCGy
        default:
            throw new Error(`Unsupported unit: ${selectedDoseUnit}`);
    }
}


/** Type guard for RadiationDoseValue */
export const isRadiationDoseValue = (value: unknown): value is RadiationDoseValue => {
    return !!value && has(value, '_doseInGy') && typeof value._doseInGy === 'number';
}

/** Type guard for LengthValue */
export const isLengthValue = (value: unknown): value is LengthValue => {
    return !!value&& has(value, '_lengthInMm')  && typeof value._lengthInMm === 'number';
}

/** Type guard for VolumeValue */
export const isVolumeValue = (value: unknown): value is VolumeValue => {
    return !!value&& has(value, '_volumeInMm3')  && typeof value._volumeInMm3 === 'number';
}
